import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import {
  LayoutDashboard, Router, Users, ListChecks, Globe,
  PlayCircle, Gauge, Phone, ShieldCheck, Settings, Sparkles, Cable, Network,
  Antenna, Radio,
} from 'lucide-react';

type NavItem = {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  shortcut?: string;
};

type NavSection =
  | { kind: 'item'; item: NavItem }
  | { kind: 'group'; label: string; items: NavItem[] };

const sections: NavSection[] = [
  { kind: 'item', item: { to: '/app',           icon: LayoutDashboard, label: 'NOC',        shortcut: '1' } },
  { kind: 'item', item: { to: '/app/devices',   icon: Router,          label: 'CPE',        shortcut: '2' } },
  {
    kind: 'group',
    label: 'FIBRE',
    items: [
      { to: '/app/onts', icon: Cable,   label: 'ONTs', shortcut: '3' },
      { to: '/app/olts', icon: Network, label: 'OLTs', shortcut: '4' },
    ],
  },
  {
    kind: 'group',
    label: 'COAX',
    items: [
      { to: '/app/coax/masters',   icon: Antenna, label: 'Masters',   shortcut: '' },
      { to: '/app/coax/endpoints', icon: Radio,   label: 'Endpoints', shortcut: '' },
    ],
  },
  { kind: 'item', item: { to: '/app/endpoints',      icon: Globe,      label: 'Équipements',   shortcut: '5' } },
  { kind: 'item', item: { to: '/app/customers',      icon: Users,      label: 'Customers',     shortcut: '6' } },
  { kind: 'item', item: { to: '/app/inventory',      icon: ListChecks, label: 'Inventory',     shortcut: '7' } },
  { kind: 'item', item: { to: '/app/mass-ops',       icon: PlayCircle, label: 'Mass ops',      shortcut: '8' } },
  { kind: 'item', item: { to: '/app/profiles/data',  icon: Gauge,      label: 'Forfaits data', shortcut: '9' } },
  { kind: 'item', item: { to: '/app/profiles/voice', icon: Phone,      label: 'Forfaits voix', shortcut: '0' } },
  { kind: 'item', item: { to: '/app/alerts',         icon: ShieldCheck,label: 'Alerts',        shortcut: ''  } },
];

function NavRow({ item, indent }: { item: NavItem; indent?: boolean }) {
  const { to, icon: Icon, label, shortcut } = item;
  return (
    <NavLink
      to={to}
      end={to === '/app'}
      className={({ isActive }) => clsx(
        'flex items-center gap-2.5 h-8 rounded text-sm group transition-colors pr-2',
        indent ? 'pl-5' : 'pl-2',
        isActive
          ? 'bg-surface-hi text-text'
          : 'text-text-muted hover:text-text hover:bg-surface-hi/50',
      )}
    >
      <Icon size={14} className="shrink-0" />
      <span className="flex-1">{label}</span>
      {shortcut ? (
        <span className="text-text-faint text-[10px] font-mono opacity-0 group-hover:opacity-100 transition-opacity">⌘{shortcut}</span>
      ) : null}
    </NavLink>
  );
}

export function Sidebar() {
  return (
    <aside className="w-52 shrink-0 border-r border-border bg-bg-2 flex flex-col">
      <div className="h-12 px-4 flex items-center gap-2 border-b border-border">
        <div className="w-5 h-5 rounded-full bg-brand animate-firefly" />
        <span className="font-semibold tracking-tight">Filos<span className="text-text-faint font-normal"> · NMS</span></span>
      </div>

      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {sections.map((s, idx) => {
          if (s.kind === 'item') {
            return <NavRow key={s.item.to} item={s.item} />;
          }
          return (
            <div key={`${s.label}-${idx}`} className="pt-1.5 first:pt-0">
              <div className="px-2 pb-0.5 text-[10px] font-semibold tracking-wider text-text-faint uppercase">
                {s.label}
              </div>
              {s.items.map((item) => <NavRow key={item.to} item={item} indent />)}
            </div>
          );
        })}
      </nav>

      <div className="p-2 border-t border-border">
        <NavLink to="/app/settings" className="flex items-center gap-2.5 px-2 h-8 rounded text-sm text-text-muted hover:text-text hover:bg-surface-hi/50">
          <Settings size={14} />
          <span>Settings</span>
        </NavLink>
        <button className="w-full mt-1 flex items-center gap-2.5 px-2 h-8 rounded text-sm text-brand hover:bg-brand-dim">
          <Sparkles size={14} />
          <span>AI assist</span>
        </button>
      </div>
    </aside>
  );
}
